import authInstance from "../../Instances/axios-auth";
import axiosTokenInstance from "../../Instances/axiosTokenInstance";

// Helper function to check if submissionId is valid
const isValidSubmissionId = (submissionId) => {
  return submissionId && submissionId !== 'undefined' && submissionId !== 'null';
};

// Unauthenticated call
export const createUser = async (userData) => {
  const requestData = {
    email: userData.email || "",
    password: userData.password || "",
    fullName: userData.userName || "",
    phoneNumber: userData.phoneNumber || null,
    companyName: userData.companyName || "",
    orgNo: userData.orgNr || "",
    postalAddress: userData.adress || "",
    postalNumber: userData.postnummer || "",
    postalPlace: userData.poststed || "",
    departmentId: 0, // Hardcoded as specified
    countryCode: "NO",
    accountNo: userData.accountNumber || "",
    clientSystem:
      userData.selectedFinancialSystem === null
        ? null
        : String(userData.selectedFinancialSystem),
    otherFinancialSystemName: userData.otherFinancialSystemName || null,
    defaultProductNo: userData.selectedService
      ? parseInt(userData.selectedService, 10)
      : null,
    vatRequired: userData.mva === "true",
    documentUrl: userData.documentUrl || null,
  };

  console.log("createUser requestData:", requestData); // Debug log
  return authInstance.post("/api/onboarding/create-user", requestData);
};

// Behalf-mode endpoints (authenticated)
export const createClientUser = async (userData) => {
  const requestData = {
    email: userData.email || "",
    password: userData.password || "",
    fullName: userData.userName || "",
    phoneNumber: userData.phoneNumber || null,
    companyName: userData.companyName || "",
    orgNo: userData.orgNr || "",
    postalAddress: userData.adress || "",
    postalNumber: userData.postnummer || "",
    postalPlace: userData.poststed || "",
    departmentId: 0,
    countryCode: "NO",
    accountNo: userData.accountNumber || "",
    clientSystem:
      userData.selectedFinancialSystem === null
        ? null
        : String(userData.selectedFinancialSystem),
    otherFinancialSystemName: userData.otherFinancialSystemName || null,
    defaultProductNo: userData.selectedService
      ? parseInt(userData.selectedService, 10)
      : null,
    vatRequired: userData.mva === "true",
    documentUrl: userData.documentUrl || null,
  };

  console.log("createClientUser requestData:", requestData);
  return axiosTokenInstance.post("/api/onboarding/create-client-user", requestData);
};

export const getMyClientOnboardings = () => 
  axiosTokenInstance.get("/api/onboarding/my-client-onboardings");

// Authenticated calls with submissionId support
export const getOnboardingStatus = (submissionId = null) => {
  const url = isValidSubmissionId(submissionId)
    ? `/api/onboarding/status?submissionId=${submissionId}`
    : "/api/onboarding/status";
  return axiosTokenInstance.get(url);
};

export const updateOnboardingProgress = (data, submissionId = null) => {
  const url = isValidSubmissionId(submissionId)
    ? `/api/onboarding/update?submissionId=${submissionId}`
    : "/api/onboarding/update";
  return axiosTokenInstance.patch(url, data);
};

// Multi-step completion endpoints with submissionId support
export const createKosCreditor = (sendEmail = true, submissionId = null) => {
  const url = isValidSubmissionId(submissionId)
    ? `/api/onboarding/create-kos-creditor?submissionId=${submissionId}`
    : "/api/onboarding/create-kos-creditor";
  return axiosTokenInstance.post(url, { sendEmail });
};

export const setupIntegration = (submissionId = null) => {
  const url = isValidSubmissionId(submissionId)
    ? `/api/onboarding/setup-integration?submissionId=${submissionId}`
    : "/api/onboarding/setup-integration";
  return axiosTokenInstance.post(url);
};

export const completeOnboarding = (sendEmail = true, submissionId = null) => {
  const url = isValidSubmissionId(submissionId)
    ? `/api/onboarding/complete?submissionId=${submissionId}`
    : "/api/onboarding/complete";
  return axiosTokenInstance.post(url, { sendEmail });
};

// Financial system options (unauthenticated)
export const getFinancialSystems = () =>
  authInstance.get("/api/onboarding/getFinancialSystem");

// Integration configuration endpoints (authenticated)
export const getIntegrationFields = (systemId) =>
  axiosTokenInstance.get(`/api/onboarding/integration-fields/${systemId}`);

// Document signing endpoints (authenticated)
/**
 * Initiates document signing process
 * @typedef {Object} InitiateSigningResponse
 * @property {string} signingUrl - URL to the signing service
 * @property {string} envelopeId - Unique identifier for the signing envelope
 * @property {'Pending'} status - Signing status (always 'Pending' on initiation)
 * @property {string} message - User-friendly message about the initiation
 *
 * @param {string} documentUrl - URL of the document to be signed
 * @returns {Promise<{data: InitiateSigningResponse}>} Axios response with signing details
 */
export const initiateDocumentSigning = (documentUrl) =>
  axiosTokenInstance.post("/api/onboarding/initiate-signing", { documentUrl });
export const getSigningStatus = () =>
  axiosTokenInstance.get("/api/onboarding/signing-status");

// Email confirmation polling endpoint (unauthenticated)
export const checkEmailConfirmationStatus = (credentials) =>
  authInstance.post("/api/onboarding/email-confirmation-status", credentials);

// Resend email confirmation endpoint (unauthenticated)
export const resendEmailConfirmation = (credentials) =>
  authInstance.post("/api/onboarding/resend-email-confirmation", credentials);

// Company registry service methods
const COMPANY_REGISTRY_BASE_URL = "https://data.brreg.no/enhetsregisteret/api/enheter";
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const retryFetch = async (url, options = {}, retries = MAX_RETRIES) => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url, options);
      
      if (!response.ok) {
        if (response.status >= 500 && attempt < retries) {
          await sleep(RETRY_DELAY * attempt);
          continue;
        }
        
        // Create error structure similar to axios response format for consistency
        const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
        error.response = {
          status: response.status,
          data: { message: response.statusText }
        };
        throw error;
      }
      
      return response;
    } catch (error) {
      if (attempt === retries) {
        throw error;
      }
      
      // Retry on network errors
      if (error.name === 'TypeError' || error.message.includes('fetch')) {
        await sleep(RETRY_DELAY * attempt);
        continue;
      }
      
      throw error;
    }
  }
};

// Search companies by name from Brønnøysund registry
export const searchCompaniesByName = async (searchTerm) => {
  if (!searchTerm || searchTerm.length < 2) {
    return [];
  }

  const apiUrl = `${COMPANY_REGISTRY_BASE_URL}?navn=${encodeURIComponent(searchTerm)}`;
  const response = await retryFetch(apiUrl);
  const data = await response.json();
  
  const companies = data._embedded?.enheter || [];
  return companies.slice(0, 10); // Limit results
};

// Get company details by organization number from Brønnøysund registry
export const getCompanyByOrgNumber = async (orgNumber) => {
  if (!orgNumber) {
    const error = new Error("Organisasjonsnummer er påkrevd");
    error.response = {
      status: 400,
      data: { message: "Organisasjonsnummer er påkrevd" }
    };
    throw error;
  }

  const cleanOrgNumber = orgNumber.replace(/\s/g, '');
  const apiUrl = `${COMPANY_REGISTRY_BASE_URL}/${cleanOrgNumber}`;
  const response = await retryFetch(apiUrl);
  return response.json();
};

